<script lang="ts" setup>
import type { IProps } from './Hero.types'

const { video, type = 'primary', isCentered = false } = defineProps<IProps>()
const { isMobile } = useBSBreakpoints()
const variantType = ref(video ? 'videoLoading' : type)

</script>

<template>
  <section :class="['banner', `banner--${variantType}`, { 'banner--centered': isCentered }]">
    <template v-if="video">
      <ClientOnly>
        <VideoBackground
          :src="video"
          :poster="isMobile && mobileImage ? mobileImage : desktopImage"
          style="position: absolute; top: 0; left: 0; height: 100vh;"
          @ready="() => variantType = 'video'"
        />

        <template #fallback>
          <img
            :src="isMobile && mobileImage ? mobileImage : desktopImage"
            class="banner__cover"
          >
        </template>
      </ClientOnly>
    </template>

    <template v-else>
      <img
        :src="desktopImage"
        class="banner__cover"
      >

      <img
        v-if="isMobile && mobileImage"
        :src="mobileImage"
        :class="[
          'banner__cover',
          'banner__cover--mobile'
        ]"
      >
    </template>

    <div class="banner__inner">
      <div :class="['banner__content', `banner__content--${variantType}`]">
        <div class="banner__header">
          <h1
            :class="['banner__title', `banner__title--${variantType}`]"
            v-html="header"
          />

          <h2
            :class="['banner__subtitle', `banner__subtitle--${variantType}`]"
            v-html="subheader"
          />

          <h4
            :class="['banner__text', `banner__text--${variantType}`]"
            v-html="text"
          />
        </div>

        <div class="banner__actions">
          <UiButton
            v-if="button && button.text"
            :class="[
              'banner__button', `
              banner__button--${variantType}`
            ]"
            :variant="button.variant || 'primary'"
            :to="button.to"
            :target="button.target || '_self'"
          >
            <span
              v-if="button.icon"
              class="banner__icon"
            >

              <img
                :class="['banner__img', `banner__img--${variantType}`]"
                width="24px"
                height="24px"
                alt=""
                :src="button.icon"
              >
            </span>

            {{ button.text }}
          </UiButton>

          <UiButton
            v-if="button2 && button2.text"
            :class="[
              'banner__button', `
              banner__button--${variantType}`
            ]"
            :variant="button2.variant || 'secondary'"
            :target="button2.target || '_self'"
            :to="button2.to"
          >
            <span
              v-if="button2.icon"
              class="banner__icon"
            >
              <img
                :class="['banner__img', `banner__img--${variantType}`]"
                width="24px"
                height="24px"
                alt=""
                :src="button2.icon"
              >
            </span>

            {{ button2.text }}
          </UiButton>
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.banner {
  position: relative;
  min-height: 100vh;
  padding: 8.8rem 2.8rem 0 2.8rem;
  overflow: hidden;
  border-radius: 0 0 2.4rem 2.4rem;
  $root: &;

  &--video {
    &:after {
      position: absolute;
      top: 0;
      left: 0;
      z-index: 2;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 3, 0.5);
      content: "";
    }
  }

  &__backgroundVideo {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
    width: 100vw;
    height: 100%;
    background-color: transparent;
  }

  &__video {
    position: absolute;
    top: 0;
    left: 0;
    z-index: -1;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &--secondary {
    min-height: 44.5rem;
  }

  &__cover {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: bottom;

    &--mobile {
      z-index: 1;
    }
  }

  &__inner {
    position: relative;
    z-index: 3;
    max-width: 115.2rem;
    width: 100%;
    margin: 0 auto;
  }

  &__content {
    display: flex;
    flex-direction: column;
    height: 100%;
    font-weight: 500;
  }

  &__header {
    margin-bottom: 2.4rem;

  }

  &__title {
    margin-bottom: 1.6rem;
    font-size: 4rem;
    line-height: 4.8rem;

    &--video {
      color: $white;
      font-weight: 700;
      font-size: 4.4rem;
      line-height: 5.6rem;

      :deep() {
        span {
          color: $white;
        }
      }
    }

    :deep() {
      span {
        color: var(--span-color);
      }
    }
  }

  &__subtitle {
    @include h10;
    margin-bottom: 1.6rem;

    &--video {
      color: $white;

      :deep() {
        span {
          color: $white;
        }
      }
    }

    :deep() {
      span {
        color: var(--span-color);
      }
    }
  }

  &__actions {
    display: flex;
    flex-direction: column;
    gap: 1.6rem;

    @include atM {
      flex-direction: row;
      gap: 0.8rem;
    }
  }

  &__button {
    &--video {
      color: $text-400;
      background-color: $white;

      &:hover {
        color: $white;
        background-color: $text-400;

        .banner__img--video {
          filter: inherit;
        }
      }
    }
  }

  &__img {
    &--video {
      filter: invert(100%);
    }
  }

  &__text {
    font-size: 1.6rem;
    line-height: 2.4rem;

    &--video {
      color: $white;
      font-weight: 400;
      font-size: 2.2rem;
      line-height: 3.2rem;
    }

    :deep(.small) {
      display: block;
      margin-top: 1.4rem;
      font-size: 1.4rem;
      line-height: 2rem;
    }
  }

  &__info {
    font-size: 1.6rem;
    line-height: 2.4rem;
  }

  &__icon {
    display: inline-block;
    padding-right: 1.2rem;

    img {
      width: 24px;
      height: 24px;
    }
  }

  &.banner--centered {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: left;
  }
}

@include atXS {
  .banner {
    min-height: 52vh;

    &__cover {
      &--mobile {
        z-index: -1;
      }
    }

    &__content {
      width: 60%;
    }

    &__title {
      font-size: 4.8rem;
      line-height: 5.6rem;
    }

    &__subtitle {
      @include h10;
    }

    &__text {
      max-width: 75%;
      font-size: 1.8rem;

      :deep(.small) {
        margin-top: 1.6rem;
        font-size: 1.6rem;
        line-height: 2.4rem;
      }
    }
  }
}

@include atS {
  .banner {
    display: flex;
    align-items: center;
    min-height: 100vh;

    &--secondary {
      min-height: 51rem;
    }

    &__title {
      font-size: 5.6rem;
      line-height: 6.4rem;
    }

    &__text {
      margin-bottom: 3.2rem;
      font-size: 2rem;
      line-height: 2.8rem;

      :deep(.small) {
        margin-top: 2.4rem;
        font-size: 1.6rem;
      }
    }
  }
}

@include atM {
  .banner {
    &__header {
      margin-bottom: 4.8rem;
    }

    &__title {
      margin-bottom: 4rem;
      font-size: 6.4rem;
      line-height: 7.2rem;

      &--video {
        font-weight: 500;
      }
    }

    &__text {
      max-width: 80%;

      &--secondary {
        @include body-1-medium;
        max-width: 75%;
      }
    }
  }
}

@include atXL {
  .banner {
    &__inner {
      max-width: 134rem;
    }

    &__content {
      width: 50%;

      &--secondary {
        width: 100%;
      }
    }

    &__title {
      margin-bottom: 3.2rem;
      font-size: 8rem;
      line-height: 8.8rem;
    }

    &__subtitle {
      @include h7;
      margin-bottom: 3.2rem;
    }

    &__text {
      font-size: 2.4rem;
      line-height: 3.2rem;

      &--secondary {
        @include body-1-medium;
        max-width: 75%;
      }

      :deep(.small) {
        margin-top: 2.4rem;
        font-size: 1.8rem;
      }
    }
  }
}
</style>
